import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import axiosLib from 'axios';
import { BrickWallShield, KeyRound, SearchX } from 'lucide-react';
import { useCallback, useEffect } from 'react';

import { NoData } from '@/components/legacy-ui/NoData';
import { NO_DATA_ICON_CLASS } from '@/components/legacy-ui/utils';
import { Button } from '@/components/ui/button';
import { IntegrationCategory } from '@/features/integrations/types/category.types';
import { useAxios } from '@/hooks/useAxios';
import { AllIntegrations, useIntegration } from '@/hooks/useIntegration';
import { useQueryBuilder } from '@/sections/insights/queryBuilder/state/useQueryBuilder';
import type { SavedQuery } from '@/sections/insights/queryBuilder/types/SavedQuery';
import { useGlobalState } from '@/state/global.state';
import type { GraphQueryResponse, NodeDetails, TreeNode } from '@/types';
import { Integration } from '@/types';

import { GraphResults } from './GraphResults';
import { TableResults } from './TableResults';
import { transformTreeNodeToFlat } from './utils/extractQueryResults';

interface QueryResultsProps {
  selectedQuery: SavedQuery | null;
  onRiskKeysChange?: (keys: string[]) => void;
  onLoadingChange?: (isLoading: boolean) => void;
}

function extractRiskKeys(data: GraphQueryResponse | TreeNode[]): string[] {
  const dedupedKeys = new Set<string>();

  if (Array.isArray(data)) {
    data.forEach(tree => {
      const risks = tree?.node?.result?.risks;
      if (!Array.isArray(risks)) return;

      risks.forEach(risk => {
        if (risk?.key) {
          dedupedKeys.add(risk.key);
        }
      });
    });

    return [...dedupedKeys];
  }

  const risks = data?.risks;
  if (!Array.isArray(risks)) {
    return [];
  }

  risks.forEach(risk => {
    if (risk?.key) {
      dedupedKeys.add(risk.key);
    }
  });

  return [...dedupedKeys];
}

/**
 * QueryResults - Executes graph queries and displays results
 *
 * Uses direct axios.post call with TanStack Query for state management.
 * Displays results in either table or graph view based on query type.
 * Handles errors inline without toast notifications.
 * Implements caching with 30s staleTime for performance optimization.
 *
 * Graph queries and table-tree queries set tree: true for nested response. For
 * table-tree queries (e.g., HAS_PORT), the TreeNode[] response is transformed
 * to flat array with relationship data joined.
 * Header is rendered by parent QueryBuilderWorkstation
 */
export const QueryResults = ({
  selectedQuery,
  onRiskKeysChange,
  onLoadingChange,
}: QueryResultsProps) => {
  const { usernames } = useQueryBuilder();
  const axios = useAxios();
  const queryClient = useQueryClient();
  const { drawerState } = useGlobalState();

  const isWafCoverageQuery =
    selectedQuery?.id === 'internet-exposed-applications-without-waf';
  const {
    data: { connectedIntegrations },
    status: integrationStatus,
  } = useIntegration({ enabled: isWafCoverageQuery });
  const hasConnectedWafIntegration = connectedIntegrations.some(integration =>
    AllIntegrations[integration.member as Integration]?.tags?.includes('waf')
  );

  const isGraphMode = selectedQuery?.type === 'graph';
  const isTableTreeMode = selectedQuery?.type === 'table-tree';

  // Detect if root node is a Risk for auto-enrichment with Vulnerability data
  const rootLabels = selectedQuery?.query?.node?.labels || [];
  const isRiskRoot = rootLabels.includes('Risk');

  // Extract selected emails for query execution
  const selectedEmails = usernames.filter(u => u.isSelected).map(u => u.email);

  // All hooks MUST be called before any conditional returns
  const { data, isLoading, isFetching, isPlaceholderData, error } = useQuery({
    queryKey: [
      'query-executor',
      selectedQuery?.id,
      selectedQuery?.type,
      selectedEmails,
      selectedQuery?.query, // Include query content for cache invalidation on changes
    ],
    queryFn: async () => {
      if (!selectedQuery) return null;

      let queryPayload = { ...selectedQuery.query };

      if (selectedEmails.length > 0)
        queryPayload = { ...queryPayload, usernames: selectedEmails };

      // Auto-enrich: if root node is Risk, inject optional INSTANCE_OF -> Vulnerability
      // so vulnerability metadata (epss/cvss/kev/title) can be merged into Risk rows
      if (isRiskRoot && !isGraphMode) {
        const existingRels = queryPayload.node?.relationships || [];
        const hasInstanceOf = existingRels.some(
          r =>
            r.label === 'INSTANCE_OF' ||
            (Array.isArray(r.label) && r.label.includes('INSTANCE_OF'))
        );
        if (!hasInstanceOf) {
          queryPayload = {
            ...queryPayload,
            node: {
              ...queryPayload.node,
              relationships: [
                ...existingRels,
                {
                  label: 'INSTANCE_OF' as const,
                  target: { labels: ['Vulnerability'] } as NodeDetails,
                  optional: true,
                },
              ],
            },
          };
        }
      }

      const needsTree = isGraphMode || isTableTreeMode || isRiskRoot;

      const response = await axios.post('/my', queryPayload, {
        timeout: 30000, // 30 second timeout
        params: needsTree ? { tree: true } : {},
      });

      // Backend may return 200 with an error body (e.g. {"error":"failed to unmarshal query"})
      const responseData = response.data;
      if (responseData?.error && typeof responseData.error === 'string') {
        throw new Error(responseData.error);
      }

      return responseData as GraphQueryResponse | TreeNode[];
    },
    enabled: !!selectedQuery, // Only run query when selectedQuery exists
    staleTime: 30000, // Cache for 30 seconds (reduces API calls by 80-90%)
    gcTime: 300000, // Keep cache for 5 minutes during navigation
    retry: false, // Don't retry failed queries automatically
    placeholderData: keepPreviousData, // Keep previous data visible during re-fetch (prevents blank flash on sort/filter changes)
  });

  useEffect(() => {
    if (!onRiskKeysChange) {
      return;
    }

    if (!selectedQuery || !isRiskRoot || !data || isLoading || error) {
      onRiskKeysChange([]);
      return;
    }

    onRiskKeysChange(extractRiskKeys(data));
  }, [onRiskKeysChange, selectedQuery?.id, isRiskRoot, data, isLoading, error]);

  useEffect(() => {
    onLoadingChange?.(isLoading || isPlaceholderData);
  }, [onLoadingChange, isLoading, isPlaceholderData]);

  const invalidateQueryResults = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['query-executor'] });
  }, [queryClient]);

  // Early return AFTER all hooks are called
  if (!selectedQuery) {
    return null;
  }

  if (isLoading || isPlaceholderData) {
    return null;
  }

  if (error) {
    const errorMessage = axiosLib.isAxiosError(error)
      ? error.response?.data?.message || error.message
      : error instanceof Error
        ? error.message
        : 'An error occurred while executing the query';

    return (
      <div
        className="flex h-full w-full items-center justify-center p-6"
        role="alert"
        aria-live="polite"
        data-testid="query-executor-error"
      >
        <div className="text-center">
          <p className="text-chariot-text-primary font-semibold">
            Query Execution Failed
          </p>
          <p className="text-chariot-text-secondary mt-2 text-sm">
            {errorMessage}
          </p>
        </div>
      </div>
    );
  }

  // Handle empty results
  const isEmpty =
    !data ||
    (Array.isArray(data) && data.length === 0) ||
    (!Array.isArray(data) && (data as GraphQueryResponse).count === 0);

  if (isEmpty) {
    if (isWafCoverageQuery && integrationStatus === 'pending') {
      return null;
    }

    if (
      isWafCoverageQuery &&
      integrationStatus === 'success' &&
      !hasConnectedWafIntegration
    ) {
      return (
        <NoData
          title="No WAF Integration Connected"
          description="Connect a Web Application Firewall integration to identify unprotected applications"
          icon={<BrickWallShield className={NO_DATA_ICON_CLASS} />}
          action={
            <Button
              onClick={() =>
                drawerState.handleOpenIntegrationDrawer(
                  undefined,
                  IntegrationCategory.Firewall
                )
              }
            >
              Add WAF Integration
            </Button>
          }
        />
      );
    }

    if (selectedQuery?.id === 'internet-exposed-login-portals-without-sso') {
      return (
        <NoData
          title="No Identity Provider Connected"
          description="Connect an Identity & Access Management integration to identify login portals without SSO protection"
          icon={<KeyRound className={NO_DATA_ICON_CLASS} />}
          action={
            <Button
              onClick={() =>
                drawerState.handleOpenIntegrationDrawer(
                  undefined,
                  IntegrationCategory.IdentityAccessManagement
                )
              }
            >
              Add Identity Provider
            </Button>
          }
        />
      );
    }

    return (
      <NoData
        title="No Results"
        description="This query returned no data"
        icon={<SearchX className={NO_DATA_ICON_CLASS} />}
      />
    );
  }

  // Render view based on query type
  if (isGraphMode) {
    return <GraphResults key={selectedQuery?.id} data={data as TreeNode[]} />;
  }

  // Table view rendering (table-tree queries and auto-enriched Risk queries)
  const needsTreeTransform = isTableTreeMode || (isRiskRoot && !isGraphMode);
  if (needsTreeTransform && Array.isArray(data)) {
    // Transform TreeNode[] to flat array with relationship data joined
    const flatData = transformTreeNodeToFlat(data as TreeNode[]);

    // Extract entity type from first result
    const firstTree = (data as TreeNode[])[0];
    const entityType = firstTree?.node?.result
      ? (Object.keys(firstTree.node.result).find(
          key => key !== 'count' && key !== 'offset'
        ) as keyof GraphQueryResponse | undefined)
      : undefined;

    // Convert to GraphQueryResponse format for TableResults
    const graphResponse: GraphQueryResponse = {
      [entityType || 'assets']: flatData,
      count: flatData.length,
    } as GraphQueryResponse;

    return (
      <TableResults
        key={selectedQuery?.id}
        data={graphResponse}
        isFetching={isFetching}
        onInvalidate={invalidateQueryResults}
      />
    );
  }

  // Standard flat GraphQueryResponse
  const graphResponse = data as GraphQueryResponse;
  return (
    <TableResults
      key={selectedQuery?.id}
      data={graphResponse}
      isFetching={isFetching}
      onInvalidate={invalidateQueryResults}
    />
  );
};
